import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cleanHtmlForPreview } from '../../utils/contentCleaner';
import './CustomerSupport.css';

const CustomerSupport = () => {
  const { t } = useTranslation('navigation');
  const [activeTab, setActiveTab] = useState('tickets');
  
  // Helper functions to translate status, priority, and category
  const translateTicketStatus = (status) => {
    if (!status) return '';
    const statusMap = {
      'open': t('support.tickets.status.open'),
      'in progress': t('support.tickets.status.inProgress'),
      'resolved': t('support.tickets.status.resolved'),
      'closed': t('support.tickets.status.closed')
    };
    return statusMap[status.toLowerCase()] || status;
  };
  
  const translatePriority = (priority) => {
    if (!priority) return '';
    const priorityMap = {
      'high': t('support.tickets.filters.high'),
      'medium': t('support.tickets.filters.medium'),
      'low': t('support.tickets.filters.low')
    };
    return priorityMap[priority.toLowerCase()] || priority;
  };
  
  const translateCategory = (category) => {
    if (!category) return '';
    const categoryMap = {
      'billing': t('support.tickets.filters.billing'),
      'login issues': t('support.tickets.filters.loginIssues'),
      'courses': t('support.tickets.filters.courses'),
      'technical': t('support.tickets.filters.technical')
    };
    return categoryMap[category.toLowerCase()] || category;
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketDetail, setShowTicketDetail] = useState(false);

  // Sample data for demonstration
  const [tickets] = useState([
    {
      id: '1029',
      user: 'amina.z',
      userEmail: 'amina@example.com',
      category: 'Billing',
      priority: 'High',
      status: 'Open',
      assignedTo: 'Yassine',
      date: '2025-01-07',
      subject: 'Payment not processed',
      description: 'I tried to pay with BaridiMob but the payment is not showing in my account.',
      messages: [
        {
          id: 1,
          sender: 'amina.z',
          message: 'I tried to pay with BaridiMob but the payment is not showing in my account.',
          timestamp: '2025-01-07 10:30',
          type: 'user'
        },
        {
          id: 2,
          sender: 'Yassine',
          message: 'Hello Amina, I can see your payment attempt. Let me check the system and get back to you within 2 hours.',
          timestamp: '2025-01-07 11:15',
          type: 'agent'
        }
      ],
      internalNotes: 'Payment ID: PMT-2025-001, needs verification with BaridiMob',
      attachments: ['receipt.jpg'],
      subscription: 'Pro Student',
      platformUsage: 'Live Learning, Course Library'
    },
    {
      id: '1030',
      user: 'karim.b',
      userEmail: 'karim@example.com',
      category: 'Login Issues',
      priority: 'Medium',
      status: 'In Progress',
      assignedTo: 'Sarah A.',
      date: '2025-01-07',
      subject: 'Cannot login to my account',
      description: 'I forgot my password and the reset email is not working.',
      messages: [],
      internalNotes: '',
      attachments: [],
      subscription: 'Basic Student',
      platformUsage: 'Course Library'
    },
    {
      id: '1031',
      user: 'fatima.s',
      userEmail: 'fatima@example.com',
      category: 'Courses',
      priority: 'Low',
      status: 'Resolved',
      assignedTo: 'Yassine',
      date: '2025-01-06',
      subject: 'Course content not loading',
      description: 'The video lessons in the English course are not playing properly.',
      messages: [],
      internalNotes: 'Issue resolved - cache cleared',
      attachments: [],
      subscription: 'Pro Student',
      platformUsage: 'Live Learning, Course Library, Exams'
    }
  ]);

  const [liveChats] = useState([
    {
      id: 1,
      user: 'ahmed.m',
      userEmail: 'ahmed@example.com',
      status: 'active',
      lastMessage: 'I need help with my subscription',
      lastActivity: '2 min ago',
      assignedTo: 'Sarah A.',
      messages: [
        { sender: 'ahmed.m', message: 'Hello, I need help with my subscription', timestamp: '14:25', type: 'user' },
        { sender: 'Sarah A.', message: 'Hello Ahmed! I\'m here to help. What specific issue are you facing?', timestamp: '14:26', type: 'agent' },
        { sender: 'ahmed.m', message: 'I want to upgrade to Pro but I\'m not sure how', timestamp: '14:28', type: 'user' }
      ]
    },
    {
      id: 2,
      user: 'sara.k',
      userEmail: 'sara@example.com',
      status: 'waiting',
      lastMessage: 'Payment question',
      lastActivity: '5 min ago',
      assignedTo: null,
      messages: []
    }
  ]);

  const [knowledgeBase] = useState([
    {
      id: 1,
      title: 'How to Reset Your Password',
      category: 'Login Issues',
      language: 'English',
      views: 1247,
      helpfulVotes: 89,
      tags: ['password', 'login', 'reset'],
      content: 'Step-by-step guide to reset your password...',
      lastUpdated: '2025-01-05'
    },
    {
      id: 2,
      title: 'Payment Methods Accepted',
      category: 'Payment',
      language: 'Arabic',
      views: 892,
      helpfulVotes: 67,
      tags: ['payment', 'baridimob', 'cib', 'cash'],
      content: 'We accept various payment methods including...',
      lastUpdated: '2025-01-04'
    },
    {
      id: 3,
      title: 'How to Join Live Learning Sessions',
      category: 'Courses',
      language: 'French',
      views: 567,
      helpfulVotes: 45,
      tags: ['live', 'learning', 'sessions', 'join'],
      content: 'Learn how to join and participate in live learning sessions...',
      lastUpdated: '2025-01-03'
    }
  ]);

  const [agents] = useState([
    {
      id: 1,
      name: 'Sarah A.',
      role: 'Support Agent',
      status: 'Online',
      ticketsHandled: 86,
      avgRating: 4.8,
      email: 'sarah@nativelike.com',
      specializations: ['Billing', 'Technical Issues'],
      shift: 'Morning (8 AM - 4 PM)'
    },
    {
      id: 2,
      name: 'Yassine',
      role: 'Senior Agent',
      status: 'Online',
      ticketsHandled: 124,
      avgRating: 4.9,
      email: 'yassine@nativelike.com',
      specializations: ['Complex Issues', 'Escalations'],
      shift: 'Afternoon (12 PM - 8 PM)'
    },
    {
      id: 3,
      name: 'Fatima Z.',
      role: 'Supervisor',
      status: 'Away',
      ticketsHandled: 45,
      avgRating: 4.7,
      email: 'fatima@nativelike.com',
      specializations: ['Management', 'Training'],
      shift: 'Evening (4 PM - 12 AM)'
    }
  ]);

  const [analytics] = useState({
    ticketsToday: 256,
    avgResponseTime: '1h 22m',
    mostCommonCategory: 'Login Issues',
    userSatisfaction: 4.6,
    openTickets: 43,
    totalAgents: 8,
    onlineAgents: 5
  });



  const tabs = [
    { id: 'tickets', label: t('support.tabs.tickets'), icon: 'inbox' },
    { id: 'live-chat', label: t('support.tabs.liveChat'), icon: 'chat' },
    { id: 'knowledge-base', label: t('support.tabs.knowledgeBase'), icon: 'psychology' },
    { id: 'analytics', label: t('support.tabs.analytics'), icon: 'analytics' },
    { id: 'agents-roles', label: t('support.tabs.agentsRoles'), icon: 'people' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'tickets':
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('support.tickets.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">add</span>
                {t('support.tickets.newTicket')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('support.tickets.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <select 
                value={selectedFilter} 
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('support.tickets.filters.allTickets')}</option>
                <option value="open">{t('support.tickets.filters.open')}</option>
                <option value="in-progress">{t('support.tickets.filters.inProgress')}</option>
                <option value="resolved">{t('support.tickets.filters.resolved')}</option>
                <option value="closed">{t('support.tickets.filters.closed')}</option>
              </select>

              <select className="filter-select">
                <option value="all">{t('support.tickets.filters.allPriorities')}</option>
                <option value="high">{t('support.tickets.filters.high')}</option>
                <option value="medium">{t('support.tickets.filters.medium')}</option>
                <option value="low">{t('support.tickets.filters.low')}</option>
              </select>

              <select className="filter-select">
                <option value="all">{t('support.tickets.filters.allCategories')}</option>
                <option value="billing">{t('support.tickets.filters.billing')}</option>
                <option value="login">{t('support.tickets.filters.loginIssues')}</option>
                <option value="courses">{t('support.tickets.filters.courses')}</option>
                <option value="technical">{t('support.tickets.filters.technical')}</option>
              </select>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('support.tickets.tableHeaders.ticketId')}</th>
                    <th>{t('support.tickets.tableHeaders.user')}</th>
                    <th>{t('support.tickets.tableHeaders.category')}</th>
                    <th>{t('support.tickets.tableHeaders.priority')}</th>
                    <th>{t('support.tickets.tableHeaders.status')}</th>
                    <th>{t('support.tickets.tableHeaders.assignedTo')}</th>
                    <th>{t('support.tickets.tableHeaders.date')}</th>
                    <th>{t('support.tickets.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => (
                    <tr key={ticket.id}>
                      <td>#{ticket.id}</td>
                      <td>
                        <div className="user-info">
                          <span className="username">{ticket.user}</span>
                          <span className="email">{ticket.userEmail}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`category-badge ${ticket.category.toLowerCase().replace(' ', '-')}`}>
                          {translateCategory(ticket.category)}
                        </span>
                      </td>
                      <td>
                        <span className={`priority-badge ${ticket.priority.toLowerCase()}`}>
                          {translatePriority(ticket.priority)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>
                          {translateTicketStatus(ticket.status)}
                        </span>
                      </td>
                      <td>{ticket.assignedTo || t('support.tickets.status.unassigned')}</td>
                      <td>{ticket.date}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn view" 
                            title={t('support.tickets.actions.viewDetails')}
                            onClick={() => {
                              setSelectedTicket(ticket);
                              setShowTicketDetail(true);
                            }}
                          >
                            <span className="material-icons">visibility</span>
                          </button>
                          <button className="action-btn assign" title={t('support.tickets.actions.assign')}>
                            <span className="material-icons">person_add</span>
                          </button>
                          <button className="action-btn priority" title={t('support.tickets.actions.changePriority')}>
                            <span className="material-icons">priority_high</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Ticket Detail Modal */}
            {showTicketDetail && selectedTicket && (
              <div className="modal-overlay" onClick={() => setShowTicketDetail(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Ticket #{selectedTicket.id} - {selectedTicket.subject}</h3>
                    <button 
                      className="close-btn"
                      onClick={() => setShowTicketDetail(false)}
                    >
                      <span className="material-icons">close</span>
                    </button>
                  </div>
                  
                  <div className="modal-body">
                    <div className="ticket-info-grid">
                      <div className="ticket-details">
                        <h4>{t('support.tickets.modal.ticketInformation')}</h4>
                        <div className="info-row">
                          <span className="label">{t('support.tickets.modal.user')}</span>
                          <span className="value">{selectedTicket.user} ({selectedTicket.userEmail})</span>
                        </div>
                        <div className="info-row">
                          <span className="label">{t('support.tickets.modal.subscription')}</span>
                          <span className="value">{selectedTicket.subscription}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">{t('support.tickets.modal.platformUsage')}</span>
                          <span className="value">{selectedTicket.platformUsage}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">{t('support.tickets.modal.category')}</span>
                          <span className="value">{translateCategory(selectedTicket.category)}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">{t('support.tickets.modal.priority')}</span>
                          <span className="value">{translatePriority(selectedTicket.priority)}</span>
                        </div>
                        <div className="info-row">
                          <span className="label">{t('support.tickets.modal.status')}</span>
                          <span className="value">{translateTicketStatus(selectedTicket.status)}</span>
                        </div>
                      </div>
                      
                      <div className="ticket-messages">
                        <h4>{t('support.tickets.modal.messageHistory')}</h4>
                        <div className="messages-container">
                          {selectedTicket.messages.map((message) => (
                            <div key={message.id} className={`message ${message.type}`}>
                              <div className="message-header">
                                <span className="sender">{message.sender}</span>
                                <span className="timestamp">{message.timestamp}</span>
                              </div>
                              <div className="message-content">{message.message}</div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="response-box">
                          <textarea placeholder={t('support.tickets.modal.typeYourResponse')}></textarea>
                          <div className="response-actions">
                            <button className="template-btn">
                              <span className="material-icons">description</span>
                              {t('support.tickets.modal.templates')}
                            </button>
                            <button className="send-btn">
                              <span className="material-icons">send</span>
                              {t('support.tickets.modal.sendResponse')}
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      <div className="ticket-sidebar">
                        <div className="internal-notes">
                          <h4>{t('support.tickets.modal.internalNotes')}</h4>
                          <textarea 
                            placeholder={t('support.tickets.modal.addInternalNotes')}
                            defaultValue={selectedTicket.internalNotes}
                          ></textarea>
                        </div>
                        
                        {selectedTicket.attachments.length > 0 && (
                          <div className="attachments">
                            <h4>{t('support.tickets.modal.attachments')}</h4>
                            {selectedTicket.attachments.map((file, index) => (
                              <div key={index} className="attachment">
                                <span className="material-icons">attachment</span>
                                <span>{file}</span>
                                <button className="view-btn">
                                  <span className="material-icons">visibility</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <div className="ticket-actions">
                          <button className="action-btn resolve">
                            <span className="material-icons">check_circle</span>
                            {t('support.tickets.modal.markAsResolved')}
                          </button>
                          <button className="action-btn escalate">
                            <span className="material-icons">priority_high</span>
                            {t('support.tickets.modal.escalate')}
                          </button>
                          <button className="action-btn close">
                            <span className="material-icons">close</span>
                            {t('support.tickets.modal.closeTicket')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 'live-chat':
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('support.liveChat.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">chat</span>
                {t('support.liveChat.startNewChat')}
              </button>
            </div>

            <div className="live-chat-container">
              <div className="chat-list">
                <div className="chat-list-header">
                  <h4>{t('support.liveChat.activeConversations')}</h4>
                  <span className="online-count">{liveChats.filter(chat => chat.status === 'active').length} {t('support.liveChat.online')}</span>
                </div>
                
                <div className="chat-items">
                  {liveChats.map((chat) => (
                    <div 
                      key={chat.id} 
                      className={`chat-item ${chat.status}`}
                      onClick={() => setSelectedTicket(chat)}
                    >
                      <div className="chat-user-info">
                        <div className="user-avatar">
                          <span className="material-icons">person</span>
                        </div>
                        <div className="user-details">
                          <span className="username">{chat.user}</span>
                          <span className="email">{chat.userEmail}</span>
                        </div>
                        <div className="chat-status">
                          <span className={`status-indicator ${chat.status}`}></span>
                        </div>
                      </div>
                      
                      <div className="chat-preview">
                        <p className="last-message">{chat.lastMessage}</p>
                        <span className="last-activity">{chat.lastActivity}</span>
                      </div>
                      
                      <div className="chat-assignment">
                        {chat.assignedTo ? (
                          <span className="assigned-to">{t('support.liveChat.assignedTo')} {chat.assignedTo}</span>
                        ) : (
                          <button className="assign-btn">
                            <span className="material-icons">person_add</span>
                            {t('support.liveChat.assign')}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="chat-window">
                {selectedTicket ? (
                  <div className="chat-conversation">
                    <div className="chat-header">
                      <h4>{t('support.liveChat.chatWith')} {selectedTicket.user}</h4>
                      <div className="chat-actions">
                        <button className="action-btn">
                          <span className="material-icons">file_upload</span>
                        </button>
                        <button className="action-btn">
                          <span className="material-icons">more_vert</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="chat-messages">
                      {selectedTicket.messages.map((message, index) => (
                        <div key={index} className={`chat-message ${message.type}`}>
                          <div className="message-bubble">
                            <p>{message.message}</p>
                            <span className="message-time">{message.timestamp}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="chat-input">
                      <textarea placeholder={t('support.liveChat.typeYourMessage')}></textarea>
                      <div className="input-actions">
                        <button className="template-btn">
                          <span className="material-icons">description</span>
                        </button>
                        <button className="send-btn">
                          <span className="material-icons">send</span>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="no-chat-selected">
                    <span className="material-icons">chat</span>
                    <p>{t('support.liveChat.selectConversation')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'knowledge-base':
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('support.knowledgeBase.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">add</span>
                {t('support.knowledgeBase.createArticle')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('support.knowledgeBase.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <select 
                value={selectedFilter} 
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">{t('support.tickets.filters.allCategories')}</option>
                <option value="payment">{t('support.tickets.filters.billing')}</option>
                <option value="login">{t('support.tickets.filters.loginIssues')}</option>
                <option value="courses">{t('support.tickets.filters.courses')}</option>
                <option value="technical">{t('support.tickets.filters.technical')}</option>
              </select>

              <select className="filter-select">
                <option value="all">All Languages</option>
                <option value="english">English</option>
                <option value="arabic">Arabic</option>
                <option value="french">French</option>
              </select>
            </div>

            <div className="knowledge-base-container">
              <div className="articles-overview">
                <div className="overview-stats">
                  <div className="stat-item">
                    <span className="stat-number">{knowledgeBase.length}</span>
                    <span className="stat-label">Total Articles</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{knowledgeBase.reduce((sum, article) => sum + article.views, 0).toLocaleString()}</span>
                    <span className="stat-label">Total Views</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{knowledgeBase.reduce((sum, article) => sum + article.helpfulVotes, 0)}</span>
                    <span className="stat-label">Helpful Votes</span>
                  </div>
                </div>
              </div>

              <div className="articles-grid">
                {knowledgeBase.map((article) => (
                  <div key={article.id} className="article-card">
                    <div className="article-header">
                      <div className="article-title-section">
                        <h4 className="article-title">{article.title}</h4>
                        <div className="article-meta">
                          <span className={`category-badge ${article.category.toLowerCase().replace(' ', '-')}`}>
                            {article.category}
                          </span>
                          <span className="language-badge">{article.language}</span>
                        </div>
                      </div>
                      <div className="article-actions-header">
                        <button className="action-btn-icon edit" title={t('support.knowledgeBase.actions.editArticle')}>
                          <span className="material-icons">edit</span>
                        </button>
                        <button className="action-btn-icon view" title={t('support.knowledgeBase.actions.editArticle')}>
                          <span className="material-icons">visibility</span>
                        </button>
                        <button className="action-btn-icon delete" title={t('support.knowledgeBase.actions.deleteArticle')}>
                          <span className="material-icons">delete</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="article-content">
                      <p className="article-excerpt">{cleanHtmlForPreview(article.content, 120)}</p>
                    </div>
                    
                    <div className="article-stats">
                      <div className="stat-item">
                        <span className="material-icons stat-icon">visibility</span>
                        <span className="stat-value">{article.views.toLocaleString()}</span>
                        <span className="stat-label">views</span>
                      </div>
                      <div className="stat-item">
                        <span className="material-icons stat-icon">thumb_up</span>
                        <span className="stat-value">{article.helpfulVotes}</span>
                        <span className="stat-label">helpful</span>
                      </div>
                      <div className="stat-item">
                        <span className="material-icons stat-icon">schedule</span>
                        <span className="stat-value">{article.lastUpdated}</span>
                        <span className="stat-label">updated</span>
                      </div>
                    </div>
                    
                    <div className="article-tags">
                      <span className="tags-label">Tags:</span>
                      {article.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                    
                    <div className="article-footer">
                      <button className="btn-secondary">
                        <span className="material-icons">preview</span>
                        Preview
                      </button>
                      <button className="btn-secondary">
                        <span className="material-icons">content_copy</span>
                        Duplicate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 'analytics':
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('support.analytics.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">download</span>
                Export Report
              </button>
            </div>

            <div className="analytics-grid">
              <div className="metric-card">
                <div className="metric-icon">
                  <span className="material-icons">inbox</span>
                </div>
                <div className="metric-content">
                  <h3>{analytics.ticketsToday}</h3>
                  <p>{t('support.analytics.metrics.ticketsToday')}</p>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">
                  <span className="material-icons">schedule</span>
                </div>
                <div className="metric-content">
                  <h3>{analytics.avgResponseTime}</h3>
                  <p>{t('support.analytics.metrics.avgResponseTime')}</p>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">
                  <span className="material-icons">category</span>
                </div>
                <div className="metric-content">
                  <h3>{analytics.mostCommonCategory}</h3>
                  <p>{t('support.analytics.metrics.mostCommonCategory')}</p>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">
                  <span className="material-icons">star</span>
                </div>
                <div className="metric-content">
                  <h3>{analytics.userSatisfaction}/5</h3>
                  <p>{t('support.analytics.metrics.userSatisfaction')}</p>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">
                  <span className="material-icons">pending</span>
                </div>
                <div className="metric-content">
                  <h3>{analytics.openTickets}</h3>
                  <p>{t('support.analytics.metrics.openTickets')}</p>
                </div>
              </div>
              
              <div className="metric-card">
                <div className="metric-icon">
                  <span className="material-icons">people</span>
                </div>
                <div className="metric-content">
                  <h3>{analytics.onlineAgents}/{analytics.totalAgents}</h3>
                  <p>{t('support.analytics.metrics.onlineAgents')}</p>
                </div>
              </div>
            </div>

            <div className="charts-section">
              <div className="chart-container">
                <h4>Ticket Categories Distribution</h4>
                <div className="chart-placeholder">
                  <span className="material-icons">pie_chart</span>
                  <p>Pie Chart: Categories Distribution</p>
                </div>
              </div>
              
              <div className="chart-container">
                <h4>Ticket Volume Trends</h4>
                <div className="chart-placeholder">
                  <span className="material-icons">trending_up</span>
                  <p>Line Chart: Daily Ticket Volume</p>
                </div>
              </div>
            </div>

            <div className="filters-section">
              <h4>Filter Analytics</h4>
              <div className="filter-options">
                <select className="filter-select">
                  <option value="all">All Agents</option>
                  <option value="sarah">Sarah A.</option>
                  <option value="yassine">Yassine</option>
                  <option value="fatima">Fatima Z.</option>
                </select>
                
                <select className="filter-select">
                  <option value="all">All Plans</option>
                  <option value="basic">Basic</option>
                  <option value="pro">Pro</option>
                  <option value="school">School</option>
                </select>
                
                <select className="filter-select">
                  <option value="all">All User Types</option>
                  <option value="student">Student</option>
                  <option value="teacher">Teacher</option>
                  <option value="parent">Parent</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 'agents-roles':
        return (
          <div className="tab-content">
            <div className="content-header">
              <h3>{t('support.agents.title')}</h3>
              <button className="primary-btn">
                <span className="material-icons">person_add</span>
                {t('support.agents.addAgent')}
              </button>
            </div>

            <div className="search-filter-bar">
              <div className="search-box">
                <input
                  type="text"
                  placeholder={t('support.agents.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <span className="material-icons">search</span>
              </div>
              
              <select 
                value={selectedFilter} 
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">All Roles</option>
                <option value="agent">Support Agent</option>
                <option value="senior">Senior Agent</option>
                <option value="supervisor">Supervisor</option>
                <option value="admin">Admin</option>
              </select>

              <select className="filter-select">
                <option value="all">All Status</option>
                <option value="online">Online</option>
                <option value="away">Away</option>
                <option value="offline">Offline</option>
              </select>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('support.agents.tableHeaders.name')}</th>
                    <th>{t('support.agents.tableHeaders.role')}</th>
                    <th>{t('support.agents.tableHeaders.status')}</th>
                    <th>{t('support.agents.tableHeaders.ticketsHandled')}</th>
                    <th>{t('support.agents.tableHeaders.avgRating')}</th>
                    <th>{t('support.agents.tableHeaders.email')}</th>
                    <th>{t('support.agents.tableHeaders.specializations')}</th>
                    <th>{t('support.agents.tableHeaders.shift')}</th>
                    <th>{t('support.agents.tableHeaders.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr key={agent.id}>
                      <td>
                        <div className="agent-info">
                          <div className="agent-avatar">
                            <span className="material-icons">person</span>
                          </div>
                          <span className="agent-name">{agent.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge ${agent.role.toLowerCase().replace(' ', '-')}`}>
                          {agent.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${agent.status.toLowerCase()}`}>
                          {agent.status}
                        </span>
                      </td>
                      <td>{agent.ticketsHandled}</td>
                      <td>
                        <div className="rating">
                          <span className="stars">⭐ {agent.avgRating}</span>
                        </div>
                      </td>
                      <td>{agent.email}</td>
                      <td>
                        <div className="specializations">
                          {agent.specializations.map((spec, index) => (
                            <span key={index} className="spec-tag">{spec}</span>
                          ))}
                        </div>
                      </td>
                      <td>{agent.shift}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="action-btn edit" title={t('support.agents.actions.editAgent')}>
                            <span className="material-icons">edit</span>
                          </button>
                          <button className="action-btn status" title={t('support.agents.actions.editAgent')}>
                            <span className="material-icons">swap_horiz</span>
                          </button>
                          <button className="action-btn permissions" title={t('support.agents.actions.managePermissions')}>
                            <span className="material-icons">security</span>
                          </button>
                          <button className="action-btn delete" title={t('support.agents.actions.editAgent')}>
                            <span className="material-icons">delete</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );

      default:
        return <div className="tab-content">Select a tab to view content</div>;
    }
  };

  return (
    <div className="section" id="customer-support">
      <h2>Customer Support</h2>
      
      {/* Sticky Tabs Navigation */}
      <div className="sticky-tabs-container">
        <div className="user-management-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="material-icons">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default CustomerSupport; 